/**
 * Client for the M9 read path (`GET /api/leaderboard`, `GET /api/games/:id/summary`).
 *
 * These are aggregate/summary READS served off a path isolated from the live game
 * (FR-10.3) — no identity, no WebSocket. The response shapes are the shared DTOs
 * ({@link LeaderboardResponse}, {@link GameSummary}) so client + server can't drift.
 *
 * ROBUSTNESS (the CloudFront SPA-rewrite landmine, DESIGN M10.3): the deployed
 * distribution rewrites a real `/api/*` 404 into the SPA `index.html` (200 HTML), so
 * an unknown game id can come back as HTML rather than JSON. We treat "not JSON" as
 * "not found" (same tactic as {@link resolveGameByCode}) so the UI shows an empty
 * state instead of a `JSON.parse` crash.
 */

import type { GameSummary, LeaderboardResponse } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';

/** Fetch the global top-N leaderboard (FR-10.2). `limit` caps the rows. */
export async function fetchLeaderboard(limit?: number): Promise<LeaderboardResponse> {
  const qs = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
  const res = await fetch(`${SERVER_HTTP_URL}/api/leaderboard${qs}`);
  if (!res.ok) throw new Error(`Could not load the leaderboard (${res.status})`);
  const body = (await res.json().catch(() => undefined)) as
    LeaderboardResponse | undefined;
  // A masked-404 HTML body parses to undefined → treat as an empty board.
  return body?.entries ? body : { entries: [] };
}

/**
 * Fetch one game's post-game summary (FR-10.1), or `null` when the game is unknown
 * (a real 404, or the CloudFront-masked HTML body). Throws only on a genuine
 * server/transport error so the caller can distinguish "no such game" from "down".
 */
export async function fetchGameSummary(gameId: string): Promise<GameSummary | null> {
  const res = await fetch(
    `${SERVER_HTTP_URL}/api/games/${encodeURIComponent(gameId)}/summary`,
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Could not load the game summary (${res.status})`);
  const body = (await res.json().catch(() => undefined)) as GameSummary | undefined;
  // A masked-404 HTML body (no gameId) → treat as not found, not a crash.
  return body?.gameId ? body : null;
}
